import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook that returns elapsed time since a given timestamp
 * Updates every second automatically
 */
export function useElapsedTime(startTime: number): string {
  const [elapsed, setElapsed] = useState<string>('');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const updateElapsed = () => {
      const now = Date.now();
      const diffMs = now - startTime;
      setElapsed(formatElapsedTime(diffMs));
    };

    // Update immediately
    updateElapsed();

    // Then update every second
    intervalRef.current = setInterval(updateElapsed, 1000);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [startTime]);

  return elapsed;
}

/**
 * Format milliseconds into mm:ss format for arbitrage opportunities
 * Examples: "00:05", "01:30", "12:45", "59:59"
 * After 60 minutes, shows 60:00+ to indicate stale opportunity
 */
export function formatElapsedTime(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  
  // If over 60 minutes, show as 60:00+ to indicate stale
  if (minutes >= 60) {
    return '60:00+';
  }
  
  // Format as mm:ss (e.g., 05:23, 12:45)
  const formattedMinutes = minutes.toString().padStart(2, '0');
  const formattedSeconds = seconds.toString().padStart(2, '0');
  
  return `${formattedMinutes}:${formattedSeconds}`;
}

/**
 * Get relative time string (e.g., "2 minutes ago", "1 hour ago")
 * Useful for tooltips or alternative displays
 */
export function getRelativeTime(timestamp: number, t?: (key: string, options?: any) => string): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const seconds = Math.floor(diffMs / 1000);
  
  // If no translation function provided, use English defaults
  if (!t) {
    if (seconds < 5) return 'just now';
    if (seconds < 60) return `${seconds} seconds ago`;
    
    const minutes = Math.floor(seconds / 60);
    if (minutes === 1) return '1 minute ago';
    if (minutes < 60) return `${minutes} minutes ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return '1 hour ago';
    if (hours < 24) return `${hours} hours ago`;
    
    const days = Math.floor(hours / 24);
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  }
  
  // Use translations
  if (seconds < 5) return t('time.justNow');
  if (seconds < 60) return t('time.secondsAgo', { count: seconds });
  
  const minutes = Math.floor(seconds / 60);
  if (minutes === 1) return t('time.minuteAgo');
  if (minutes < 60) return t('time.minutesAgo', { count: minutes });
  
  const hours = Math.floor(minutes / 60);
  if (hours === 1) return t('time.hourAgo');
  if (hours < 24) return t('time.hoursAgo', { count: hours });
  
  const days = Math.floor(hours / 24);
  if (days === 1) return t('time.dayAgo');
  return t('time.daysAgo', { count: days });
}